
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { defineComponent, ref } from "vue";
import { getGraphData, getGraphDataDates } from "@/service/datastation.service";
import moment from "moment";
import { Image } from "@/types/Image.type";
import { getImageByFile, getLastImagesByDate } from "@/service/image.service";
import { ElMessage } from "element-plus";
import StationImageComponent from "./StationImage.component.vue";
import securityService from "@/service/security.service";
import { AppRole } from "@/types/app.type";
import { reactive } from "vue";
import am5lang_es_ES from "@amcharts/amcharts5/locales/es_ES";
import { CameraConfiguration } from "@/types/cameraconfiguration.type";
import { getStations } from "@/service/station.service";
import { Filter } from "@/types/filter.type";

export default defineComponent({
  components: {
    StationImageComponent,
  },
  setup() {
    const chart = "" as unknown as am5xy.XYChart;
    // Leyenda
    const legend = "" as unknown as am5.Legend;
    // Id grafica
    const chartId = "chartdiv";
    // Datos de las fotos para graficar
    const series = "" as unknown as am5xy.LineSeries;
    // Datos de las fotos para los scrolls
    const sbseries = "" as unknown as am5xy.LineSeries;
    const label = "" as unknown as am5.Label;

    const visible = ref(false);
    const isAppDemo = securityService.hasRole(AppRole.DEMOS) ? true : false;
    return {
      chart,
      legend,
      chartId,
      series,
      sbseries,
      label,
      visible,
      isAppDemo,
    };
  },
  data() {
    return {
      startDate: new Date(),
      endDate: new Date(),
      dataMin: undefined as number | undefined,
      dataMax: undefined as number | undefined,
      values: {},
      lastImage: undefined as Image | undefined,
      fileNames: undefined as string[] | undefined,
      datesWithData: reactive<string[]>([]),
      dateKey: 0,
      currentIndex: undefined as number | undefined,

      selectedDate: undefined as number | undefined,
      clickedImages: [] as Image[],
      selectedIndex: 0,
      nearbyImages: [] as any[],

      cameras: [] as CameraConfiguration[],
      night: undefined as number | undefined,
    };
  },
  props: {
    station: { type: Object, required: true, immediate: true, deep: true },
    parameter: { type: String, required: true, immediate: true, deep: true },
  },
  // mounted() {
  //   this.showLastTwentyFour();
  //   this.getMarkedDates();
  //   window.addEventListener("keydown", this.handleKey);
  // },
  async mounted() {
    await this.getMarkedDates();
    this.showLastTwentyFour();
    this.cameras = this.station.stationsCamera;
    window.addEventListener("keydown", this.handleKey);
  },

  beforeUnmount() {
    window.removeEventListener("keydown", this.handleKey);
  },
  methods: {
    async fillData() {
      am5.array.each(am5.registry.rootElements, function (root) {
        if (root.dom.id == "chartdiv" && root !== undefined) {
          root.dispose();
        }
      });

      let root = am5.Root.new("chartdiv");
      root.locale = am5lang_es_ES;
      root.setThemes([am5themes_Animated.new(root)]);

      this.chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: true,
          panY: true,
          wheelX: "panX",
          wheelY: "zoomX",
          pinchZoomX: true,
          paddingLeft: 0,
        })
      );
      const response = await getGraphData(
        this.station.id,
        [this.parameter],
        this.startDate.toISOString(),
        this.endDate.toISOString()
      );

      let cursor = this.chart.set("cursor", am5xy.XYCursor.new(root, {}));
      cursor.lineX.set("forceHidden", true);
      cursor.lineY.set("forceHidden", true);

      if (response.length > 0) {
        let data = [{}];
        data.pop();
        response.map((_) => {
          let date = new Date(_.dateTime);
          data.push({
            date: am5.time.add(date, "minute", 1).getTime(),
            value: Math.round(parseFloat(_.measurement) * 100) / 100,
          });
        });
        const values = data;

        let xAxis = this.chart.xAxes.push(
          am5xy.DateAxis.new(root, {
            baseInterval: {
              timeUnit: "minute",
              count: 1,
            },
            renderer: am5xy.AxisRendererX.new(root, {
              minorGridEnabled: true,
            }),
          })
        );

        let yAxis = this.chart.yAxes.push(
          am5xy.ValueAxis.new(root, {
            baseValue: 0,
            renderer: am5xy.AxisRendererY.new(root, {
              minorGridEnabled: false,
            }),
          })
        );

        this.series = this.chart.series.push(
          am5xy.LineSeries.new(root, {
            name: "Series",
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: "value",
            valueXField: "date",
          })
        );

        this.series.fills.template.setAll({
          fillOpacity: 0.2,
          visible: true,
        });
        // Add bullets
        this.series.bullets.push(() => {
          var circle = am5.Circle.new(root, {
            radius: 4,
            fill: this.series.get("fill"),
            strokeWidth: 2,
            cursorOverStyle: "pointer",
            tooltipHTML: `
            <div style="display: flex; justify-content: center; align-items: center; height: 100%; text-align: center;">
                <div>
                    <div>{valueX.formatDate('dd/MM/yyyy HH:mm')}</div>
                    <div style="font-weight: bold;">Valor: {valueY.formatNumber('#,###.##')}</div>
                </div>
            </div>
          `,
          });

          circle.events.on("click", (event: any) => {
            this.getImage(event);
          });

          return am5.Bullet.new(root, {
            sprite: circle,
          });
        });
        // Add scrollbar

        this.chart.set(
          "scrollbarX",
          am5.Scrollbar.new(root, {
            orientation: "horizontal",
          })
        );

        // add series range
        let rangeDataItem = xAxis.makeDataItem({});
        xAxis.createAxisRange(rangeDataItem);

        // create container for all elements, you can put anything you want top it
        let container = am5.Container.new(root, {
          centerY: am5.p50,
          draggable: true,
          layout: root.horizontalLayout,
        });

        // this is needed for the bullets to be interactive, above the plot
        yAxis.topGridContainer.children.push(container);

        // create bullet and set container as a bullets sprite
        rangeDataItem.set(
          "bullet",
          am5xy.AxisBullet.new(root, {
            sprite: container,
          })
        );

        // decorate grid of a range
        rangeDataItem.get("grid")?.setAll({
          strokeOpacity: 1,
          visible: true,
          stroke: am5.color(0x000000),
          strokeDasharray: [2, 2],
        });

        // create background for the container
        let background = am5.RoundedRectangle.new(root, {
          fill: am5.color(0xffffff),
          fillOpacity: 1,
          strokeOpacity: 0.5,
          cornerRadiusTL: 0,
          cornerRadiusBL: 0,
          cursorOverStyle: "ns-resize",
          stroke: am5.color(0xff0000),
        });

        container.set("background", background);
        this.series.data.setAll(values);

        this.series.appear(1000);
        this.chart.appear(1000, 100);
      }
    },
    // Convierte distintos tipos a timestamp en ms para la comparacion
    convertToTimestamp(
      date: Date | string | number | moment.Moment | any | undefined
    ): number | undefined {
      if (!date) return undefined;

      if (moment.isMoment(date)) return date.toDate().getTime();
      if (date instanceof Date) return date.getTime();

      if (typeof date === "object" && date !== null) {
        if (typeof date.seconds === "number") {
          const secs = date.seconds;
          const nanos = typeof date.nanoseconds === "number" ? date.nanoseconds : 0;
          return secs * 1000 + Math.floor(nanos / 1e6);
        }
        if (typeof date.toDate === "function") {
          try {
            const d = date.toDate();
            if (d instanceof Date) return d.getTime();
          } catch (e) {
            console.log("convertToTimestamp error");
          }
        }
      }

      if (typeof date === "number") {
        if (date > 1e9 && date < 1e12) return date * 1000;
        return date;
      }

      if (typeof date === "string") {
        const s = date.trim();
        if (/^-?\d+$/.test(s)) {
          const n = Number(s);
          if (n > 1e9 && n < 1e12) return n * 1000;
          return n;
        }
        const parsed = Date.parse(s);
        if (!isNaN(parsed)) return parsed;
        const dd = new Date(s);
        if (!isNaN(dd.getTime())) return dd.getTime();
      }

      return undefined;
    },
    // encuentra el index de la imagen más cercana a un timestamp
    findClosestIndex(targetTs: number) {
      if (!this.clickedImages || !this.clickedImages.length) return -1;
      let bestIdx = 0;
      let bestDiff = Infinity;
      for (let i = 0; i < this.clickedImages.length; i++) {
        const ts = this.convertToTimestamp(this.clickedImages[i].dateTime) ?? 0;
        const diff = Math.abs(ts - targetTs);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestIdx = i;
        }
      }
      return bestIdx;
    },
    // Obtener imágenes al hacer click en un punto del gráfico
    async getImage(ev: any): Promise<void> {
      const dataContext = ev.target?.dataItem?.dataContext as { date?: Date | string | number };
      const clickedTs = this.convertToTimestamp(dataContext?.date);
      if (!clickedTs) return;

      if (this.selectedDate === clickedTs && this.visible) return;

      this.selectedDate = clickedTs;
      this.clickedImages = [];
      this.selectedIndex = 0;
      this.lastImage = undefined;

      try {
        let selectedCamera = this.cameras?.find(
          (cam: any) => cam.imageProcessingTime === "ALL_DAY"
        );

        if (!selectedCamera) {
          const eventValue = ev.target?.dataItem?.dataContext?.value;

          if (eventValue !== undefined && this.night) {
            if (this.night > eventValue) {
              selectedCamera = this.cameras?.find((cam: any) => cam.imageProcessingTime === "DAY");
            } else if (this.night < eventValue) {
              selectedCamera = this.cameras?.find(
                (cam: any) => cam.imageProcessingTime === "NIGHT"
              );
            }
          }
        }

        const { data } = await getLastImagesByDate(
          `${this.station.station}`,
          1,
          50,
          selectedCamera ? selectedCamera.camera?.code : undefined,
          new Date(clickedTs - 5 * 60 * 1000),
          new Date(clickedTs + 5 * 60 * 1000)
        );

        if (!data || data.length === 0) {
          ElMessage.warning("No hay imágenes cercanas al dato seleccionado (≤ 5 min).");
          return;
        }

        const ordenadas = data.sort(
          (a: any, b: any) =>
            this.convertToTimestamp(a.dateTime)! - this.convertToTimestamp(b.dateTime)!
        );

        this.clickedImages = ordenadas;

        const mapped = ordenadas
          .map((item: any) => {
            const imgDate = this.convertToTimestamp(item.dateTime);
            return imgDate ? { item, imgDate, diffMs: Math.abs(clickedTs - imgDate) } : null;
          })
          .filter(Boolean) as { item: any; imgDate: number; diffMs: number }[];

        const within30 = mapped
          .filter((m) => m.diffMs <= 5 * 60 * 1000)
          .sort((a, b) => a.diffMs - b.diffMs);

        if (within30.length === 0) {
          ElMessage.warning("No hay imágenes cercanas al dato seleccionado (≤ 5 min).");
          return;
        }

        const nearest = within30[0].item;
        const nearestTs = this.convertToTimestamp(nearest.dateTime)!;

        this.selectedIndex = this.clickedImages.findIndex(
          (it: any) =>
            it?.fileName === nearest.fileName && this.convertToTimestamp(it?.dateTime) === nearestTs
        );

        if (this.selectedIndex === -1) {
          this.selectedIndex = this.findClosestIndex(clickedTs);
        }

        const fileName = this.clickedImages[this.selectedIndex]?.fileName;
        if (!fileName) return;

        this.lastImage = await getImageByFile(fileName);
        this.visible = true;
      } catch (err) {
        console.error("getImage error:", err);
        ElMessage.error("Error al obtener imágenes.");
      }
    },
    // Cargar más imágenes hacia delante o atrás, sin salirse del rango
    async loadMoreImages(direction: "next" | "prev") {
      if (!this.selectedDate) return;

      try {
        const stepHours = 1;
        let attempts = 0;
        const maxAttempts = 3;
        let foundNew = false;

        while (attempts < maxAttempts && !foundNew) {
          attempts++;

          let from: Date, to: Date;

          if (direction === "next") {
            from = new Date(this.selectedDate + attempts * stepHours * 60 * 60 * 1000);
            to = new Date(this.selectedDate + (attempts + 1) * stepHours * 60 * 60 * 1000);
            if (from >= this.endDate) {
              ElMessage.warning("Esta es la última imagen dentro del rango seleccionado.");
              break;
            }
            if (to > this.endDate) to = this.endDate;
          } else {
            from = new Date(this.selectedDate - (attempts + 1) * stepHours * 60 * 60 * 1000);
            to = new Date(this.selectedDate - attempts * stepHours * 60 * 60 * 1000);
            if (to <= this.startDate) {
              ElMessage.warning("Esta es la primera imagen dentro del rango seleccionado.");
              break;
            }
            if (from < this.startDate) from = this.startDate;
          }

          const { data } = await getLastImagesByDate(
            `${this.station.station}`,
            1,
            50,
            undefined,
            from,
            to
          );

          if (data && data.length > 0) {
            const ordenadas = data
              .slice()
              .sort(
                (a: any, b: any) =>
                  this.convertToTimestamp(a.dateTime)! - this.convertToTimestamp(b.dateTime)!
              );

            const nuevos = ordenadas.filter(
              (img: any) => !this.clickedImages.some((c) => c.fileName === img.fileName)
            );

            if (nuevos.length > 0) {
              this.clickedImages = [...this.clickedImages, ...nuevos]
                .filter((v, i, a) => a.findIndex((x) => x.fileName === v.fileName) === i)
                .sort(
                  (a: any, b: any) =>
                    this.convertToTimestamp(a.dateTime)! - this.convertToTimestamp(b.dateTime)!
                );

              const prevIndex = this.selectedIndex;
              this.selectedIndex = this.findClosestIndex(this.selectedDate);

              if (this.selectedIndex === -1) this.selectedIndex = 0;

              const fileName = this.clickedImages[this.selectedIndex]?.fileName;
              if (fileName) {
                this.lastImage = await getImageByFile(fileName);
                this.selectedDate = this.convertToTimestamp(
                  this.clickedImages[this.selectedIndex].dateTime
                );
              }

              foundNew = true;
              break;
            }
          }
        }

        // if (!foundNew) {
        //   ElMessage.info("No se han encontrado más imágenes en las ventanas siguientes.");
        // }
      } catch (err) {
        console.error("loadMoreImages error:", err);
      }
    },
    async prevImg() {
      if (!this.clickedImages.length) return;

      if (this.selectedIndex > 0) {
        this.selectedIndex--;
      } else {
        await this.loadMoreImages("prev");
        if (this.selectedIndex <= 0 && this.clickedImages.length > 1 && this.selectedDate) {
          this.selectedIndex = this.findClosestIndex(this.selectedDate);
        }
        if (this.selectedIndex === 0 && this.clickedImages.length === 1) {
          //
        }
      }

      const fileName = this.clickedImages[this.selectedIndex]?.fileName;
      if (!fileName) {
        // ElMessage.error("La imagen no tiene nombre de archivo.");
        return;
      }

      this.lastImage = await getImageByFile(fileName);
      this.selectedDate = this.convertToTimestamp(this.clickedImages[this.selectedIndex].dateTime);
    },
    async nextImg() {
      if (!this.clickedImages.length) return;

      if (this.selectedIndex < this.clickedImages.length - 1) {
        this.selectedIndex++;
      } else {
        await this.loadMoreImages("next");
        if (this.selectedDate) this.selectedIndex = this.findClosestIndex(this.selectedDate);
      }

      const fileName = this.clickedImages[this.selectedIndex]?.fileName;
      if (!fileName) {
        // ElMessage.error("La imagen no tiene nombre de archivo.");
        return;
      }

      this.lastImage = await getImageByFile(fileName);
      this.selectedDate = this.convertToTimestamp(this.clickedImages[this.selectedIndex].dateTime);
    },
    handleKey(event: { key: string }) {
      if (event.key === "ArrowRight") {
        this.nextImg();
      }
      if (event.key === "ArrowLeft") {
        this.prevImg();
      }
    },
    async getMarkedDates() {
      try {
        const filter: Filter = {};

        filter.fields = {};

        filter.fields["station.station"] = {
          operator: "contains",
          value: this.station.station,
        };
        const { data } = await getStations(1, 1, filter);

        const response1 = await getGraphDataDates(this.station.id, [this.parameter]);

        // const dates = response1.map((data: any) => {
        //   const date = new Date(data.dateTime);
        //   const dateString = date.toDateString();
        //   return dateString;
        // });
        const dates = response1.map((data: any) => moment(data.dateTime).format("YYYY-MM-DD"));

        this.datesWithData.splice(0, this.datesWithData.length, ...dates);
        this.night = data[0].night;
        this.dateKey++;
      } catch (error) {
        console.error("Error fetching dates:", error);
      }
    },
    // hasEstimatedData(day: Date) {
    //   return this.datesWithData.includes(day.toDateString());
    // },
    // disabledData(date: Date) {
    //   return !this.datesWithData.includes(date.toDateString());
    // },
    disabledData(date: Date) {
      const dateStr = moment(date).format("YYYY-MM-DD");
      return !this.datesWithData.includes(dateStr);
    },
    hasEstimatedData(date: Date) {
      const dateStr = moment(date).format("YYYY-MM-DD");
      return this.datesWithData.includes(dateStr);
    },

    showLastTwentyFour() {
      this.calculateBeforeDay("H");
      this.removeBlueClass();
      this.addBlue("btnDay");
      this.fillData();
    },
    showLastWeek() {
      this.calculateBeforeDay("W");
      this.removeBlueClass();
      this.addBlue("btnWeek");
      this.fillData();
    },
    showLastMonth() {
      this.calculateBeforeDay("M");
      this.removeBlueClass();
      this.addBlue("btnMonth");
      this.fillData();
    },
    calculateBeforeDay(unit: string) {
      this.endDate = new Date();
      if (unit === "H") {
        this.startDate = moment(this.endDate).add(-24, "h").toDate();
      } else if (unit === "W") {
        this.startDate = moment(this.endDate).add(-1, "w").toDate();
      } else if (unit === "M") {
        this.startDate = moment(this.endDate).add(-1, "M").toDate();
      }
    },
    showRangeDateButton() {
      this.removeBlueClass();
      this.addBlue("btnRangeDate");
    },
    addBlue(idBtn: string) {
      const btn = document.getElementById(idBtn);
      btn?.classList.add("blue");
    },
    removeBlueClass() {
      let blueBtn = document.getElementsByClassName("blue");
      blueBtn[0].className = blueBtn[0].className.replace("blue", "");
    },
    async confirmRangeDate() {
      this.fillData();
    },
    seeImages() {
      this.visible = true;
    },
  },
  watch: {
    parameter: {
      // immediate: true
      deep: true,
      handler(newVal) {
        this.showLastTwentyFour();
      },
    },
    // parameter: function () {
    //   this.showLastTwentyFour();
    // },
  },
});
